import React, {useEffect, useMemo, useState} from 'react'
import Typography from '@material-ui/core/Typography';
import Tr from 'translations/Translations';
import Dialog from 'components/Dialog';
import GridBlock from 'components/GridBlock';
import Form from 'components/Form';
import * as Yup from 'yup';
import useId from 'hooks/useId';
import {makeStyles} from "@material-ui/core";
import VS from "constants/ValidationShema";
import TextInput from "components/TextInput";
import Loading from "components/Loading";
import CorrespondenceService from "services/CorrespondenceService";
import OrganizationsSelect from "components/OrganizationsSelect";
import InputLimits from "constants/InputLimits";
import UploadFile from "components/UploadFile";
import Box from "@material-ui/core/Box";
import MultiButton, {createButton} from "components/popup/components/MultiButton";
import isResponseValid from "libs/isResponseValid";
import contractorNameBuilder from "libs/contractorNameBuilder";
import ToggleButton from "@material-ui/lab/ToggleButton";
import ToggleButtonGroup from "@material-ui/lab/ToggleButtonGroup";
import TypographyWithIcon from "components/TypographyWithIcon";
import InfoIcon from '@material-ui/icons/Info';
import Button from "components/Button";
import CorrespondenceEmailList from "./CorrespondenceEmailList";
import useSnackbar from "hooks/useSnackbar";
import {connect} from "react-redux";

const useStyle = makeStyles(theme => ({
    marginTop: {
        marginTop: theme.spacing(4)
    },
    marginLeft: {
        marginLeft: theme.spacing(2)
    },
    loading: {
        width: '98%'
    },
    buttonRow: {
        textAlign: "end"
    },
    uploadFile: {
        marginBottom: 20
    },
    toggle: {
        width: "100%"
    },
    toggleButton: {
        width: "50%"
    },
    dialogContent: {
        position: "relative"
    },
    ctrEmailList: {
        background: theme.palette.background.paper,
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%"
    },
    ctrEmailListTitle: {
        position: "fixed",
        background: theme.palette.background.paper,
        maxWidth: 900,
        marginTop: theme.spacing(-1)
    }
}));

const limits = InputLimits.procedure.correspondence;

function AddThreadPopup(
    {
        rfxRoundId,
        listResourceId,
        onClose,
        contractor,
        formSubmit,
    }) {

    const classes = useStyle();

    const [error, setError] = useState('');
    const [sendType, setSendType] = useState('');
    const [working, setWorking] = useState(!contractor);
    const [msgSent, setMsgSent] = useState(false);
    const [withOfferSwitch, setWithOfferSwitch] = useState(false);
    const [contractorList, setContractorList] = useState([]);
    const [contractorEmailList, setContractorEmailList] = useState(null);
    const {enqueueErrorSnackbar} = useSnackbar();
    const formId = useId();

    const contractorListForSelect = useMemo(() => {
        const srcList = withOfferSwitch ? contractorList.filter(x => x.hasOffer) : contractorList;
        return srcList.map(x => ({label: contractorNameBuilder(x), value: x.id}));
    }, [contractorList, withOfferSwitch]);

    const handleSubmit = (type) => {
        setError('');
        setSendType(type);
        formSubmit[formId]();
    }

    useEffect(() => {
        if (!contractor) {
            const contractorInRoundList = CorrespondenceService.getBuyerContractorInRoundList(rfxRoundId).then(resp => setContractorList(resp));
            Promise.all([contractorInRoundList]).finally(() => setWorking(false));
        }

    }, [rfxRoundId, contractor]);

    const handleSaveResponse = (response) => {
        if (isResponseValid(response, setError)) {
            setWorking(false);
            onClose();
        } else {
            setWorking(false);
        }
    };

    const handleSendResponse = (saveResponse) => {
        if (isResponseValid(saveResponse, setError)) {
            const messages = saveResponse.messages;
            if (!messages || messages.length === 0 || !messages[0].messageId || !saveResponse.threadId) {
                setError(Tr('unknown_error_message'));
            } else {
                if (contractor) {
                    CorrespondenceService.sendContractorMessage(rfxRoundId, saveResponse.threadId, messages[0].messageId, listResourceId).then(handleSaveResponse).catch(() => setWorking(false))
                } else {
                    CorrespondenceService.sendBuyerMessage(rfxRoundId, saveResponse.threadId, messages[0].messageId, listResourceId).then(handleSaveResponse).catch(() => setWorking(false))
                }
            }
        } else {
            setWorking(false);
        }
    };

    const onSubmit = (thread) => {
        setWorking(true);
        setMsgSent(true);
        const req = {
            subject: thread.subject,
            message: {
                attachments: thread.attachments,
                contents: thread.contents,
                recipients: contractor ? [] : contractorList.filter(x => thread.supplierRecipientIds.indexOf(x.id) > -1)
            }
        };

        if (sendType === 'save') {
            if (contractor) {
                CorrespondenceService.addContractorThread(rfxRoundId, req, listResourceId).then(handleSaveResponse).catch(() => setWorking(false));
            } else {
                CorrespondenceService.addBuyerThread(rfxRoundId, req, listResourceId).then(handleSaveResponse).catch(() => setWorking(false));
            }
        } else if (sendType === 'send') {
            if (contractor) {
                CorrespondenceService.addContractorThread(rfxRoundId, req, listResourceId).then(handleSendResponse).catch(() => setWorking(false));
            } else {
                CorrespondenceService.addBuyerThread(rfxRoundId, req, listResourceId).then(handleSendResponse).catch(() => setWorking(false));
            }

        }
        setSendType('');

    }

    const getButtons = () => {
        const buttons = [];
        buttons.push(createButton(Tr('cancel'), onClose, 'outlined', working || !!contractorEmailList));
        buttons.push(createButton(Tr('save'), () => handleSubmit('save'), 'outlined', working || msgSent || !!contractorEmailList));
        buttons.push(createButton(Tr('send'), () => handleSubmit('send'), undefined, working || msgSent || !!contractorEmailList));
        return buttons;
    }

    const onChangeWithOffer = (withOffer, formikProps) => {
        if (withOffer !== null && withOffer !== withOfferSwitch) {
            formikProps.setFieldValue('supplierRecipientIds', []);
            setWithOfferSwitch(withOffer)
        }
    }

    const chipClick = (e) => {
        setContractorEmailList({});
        const ctr = contractorList.find(x => contractorNameBuilder(x) === e.target.innerText);
        CorrespondenceService.getBuyerContractorEmailList(rfxRoundId, ctr.contractorDepartmentId).then(setContractorEmailList).catch(() => {
            setContractorEmailList(null)
            enqueueErrorSnackbar(Tr("unknown_error_message"));
        })

    }

    return (
        <Dialog
            title={Tr("new_message")}
            open={true}
            maxWidth='md'
            dialogActions={<MultiButton buttons={getButtons()}/>}
        >
            <div className={classes.dialogContent}>
                <Form
                    initialValues={{
                        subject: "",
                        contents: "",
                        supplierRecipientIds: [],
                        attachments: []
                    }}
                    validationSchema={Yup.object().shape({
                        subject: VS.stringMax(limits.SUBJECT),
                        contents: VS.string(),
                        supplierRecipientIds: contractor ? VS.any() : VS.arrayRequired()
                    })}
                    onSubmit={onSubmit}
                    id={formId}
                >
                    {(formikProps) => (
                        <GridBlock column>
                            {working && <Loading className={classes.loading}/>}

                            {error && <Typography color='error'>{error}</Typography>}

                            {!contractor &&
                                <ToggleButtonGroup className={classes.toggle} size="small" value={withOfferSwitch} exclusive
                                                   onChange={(e, v) => onChangeWithOffer(v, formikProps)}>
                                    <ToggleButton classes={{root: classes.toggleButton}} value={false} disabled={working}>
                                        {Tr("correspondence_all_suppliers")}
                                    </ToggleButton>
                                    <ToggleButton classes={{root: classes.toggleButton}} value={true} disabled={working}>
                                        {Tr("correspondence_suppliers_with_offer")}
                                    </ToggleButton>
                                </ToggleButtonGroup>
                            }
                            {!contractor &&
                                <OrganizationsSelect
                                    id='supplierRecipientIds'
                                    formikProps={formikProps}
                                    label={Tr('choose_recipients')}
                                    disabled={working}
                                    items={contractorListForSelect}
                                    className={classes.uploadFile}
                                    required
                                    onChipClick={chipClick}
                                    chipTitle={Tr("correspondence_email_title")}
                                />
                            }

                            <TextInput
                                id='subject'
                                formikProps={formikProps}
                                label={Tr('subject')}
                                disabled={working}
                                limit={limits.SUBJECT}
                                required
                            />

                            <TextInput
                                id='contents'
                                formikProps={formikProps}
                                label={Tr('contents')}
                                disabled={working}
                                multiline
                                required
                            />

                            <Box component="div" className={classes.uploadFile}>
                                <UploadFile
                                    id='attachments'
                                    formikProps={formikProps}
                                    label={Tr("attachment")}
                                    disabled={working}
                                />
                            </Box>

                        </GridBlock>
                    )}
                </Form>
                {!!contractorEmailList && <div className={classes.ctrEmailList}>
                    <GridBlock alignItems="center" className={classes.ctrEmailListTitle}>
                        <TypographyWithIcon Icon={InfoIcon}>{Tr("correspondence_email_notification_list")}</TypographyWithIcon>
                        <Button onClick={()=> setContractorEmailList(null)} disabled={!contractorEmailList.recipient} className={classes.marginLeft}>{Tr("close")}</Button>
                    </GridBlock>
                    {
                        !contractorEmailList.recipient ? <Loading/> : <CorrespondenceEmailList className={classes.marginTop} wrapper={contractorEmailList}/>
                    }

                </div>
                }
            </div>
        </Dialog>)
}

export default connect((state) => ({
    formSubmit: state.form
}), {})(AddThreadPopup);